import { normalizeEmail } from "../../../lib/utils";
import { normalizeVoteValue, VOTE_VALUES } from "../../../lib/vote-utils";
import { normalizeVoterIdentity } from "../../../lib/voter-identity";

function toMs(iso) {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}
function slotWindow(slot) {
  const startMs = slot?.start ? toMs(slot.start) : null;
  const endMs = slot?.end ? toMs(slot.end) : null;
  if (startMs == null || endMs == null) return null;
  return { startMs, endMs };
}

export function buildEffectiveTallies({
  slots = [],
  voteDocs = [],
}) {
  const windowsBySlotId = new Map();
  (slots || []).forEach((slot) => {
    const win = slotWindow(slot);
    if (win) windowsBySlotId.set(slot.id, win);
  });

  const tallies = {};
  const slotVoters = {};

  (voteDocs || []).forEach((voteDoc) => {
    if (!voteDoc?.id) return;
    if (voteDoc.noTimesWork) return;
    // Build a normalized voter stub keyed on uid/id (with guestName/displayName
    // fallbacks) so a guest — who has no email — still renders. Preserve the
    // downstream `source` indicator (web/discord). See lib/voter-identity.js.
    const userInfo = {
      ...normalizeVoterIdentity(voteDoc),
      source: voteDoc.source || voteDoc.lastVotedFrom || "web",
    };

    Object.entries(voteDoc.votes || {}).forEach(([slotId, rawValue]) => {
      const voteValue = normalizeVoteValue(rawValue);
      if (voteValue !== VOTE_VALUES.FEASIBLE && voteValue !== VOTE_VALUES.PREFERRED) return;
      const win = windowsBySlotId.get(slotId);
      if (!win) return;

      if (!tallies[slotId]) tallies[slotId] = { feasible: 0, preferred: 0 };
      if (!slotVoters[slotId]) slotVoters[slotId] = { feasible: [], preferred: [] };

      if (voteValue === VOTE_VALUES.PREFERRED) {
        tallies[slotId].preferred += 1;
        tallies[slotId].feasible += 1;
        slotVoters[slotId].preferred.push(userInfo);
        slotVoters[slotId].feasible.push(userInfo);
      } else {
        tallies[slotId].feasible += 1;
        slotVoters[slotId].feasible.push(userInfo);
      }
    });
  });

  // Dedupe voter lists by id (preferred) with an email fallback, preserving
  // first occurrence. A voter with neither id nor email (shouldn't happen, but
  // defensive) is still kept exactly once — never dropped for lacking an email.
  Object.entries(slotVoters).forEach(([slotId, lists]) => {
    const seenPreferred = new Set();
    const seenFeasible = new Set();
    const preferred = [];
    const feasible = [];

    (lists.preferred || []).forEach((user) => {
      const key = user?.id || normalizeEmail(user?.email);
      if (!key) {
        preferred.push(user);
        return;
      }
      if (seenPreferred.has(key)) return;
      seenPreferred.add(key);
      preferred.push(user);
    });

    (lists.feasible || []).forEach((user) => {
      const key = user?.id || normalizeEmail(user?.email);
      if (!key) {
        feasible.push(user);
        return;
      }
      if (seenFeasible.has(key)) return;
      seenFeasible.add(key);
      feasible.push(user);
    });

    slotVoters[slotId] = { preferred, feasible };
  });

  return { tallies, slotVoters, windowsBySlotId };
}

/**
 * Per-slot block info for the user. Pre-Chunk-D this looked at busyWindows;
 * post-Chunk-D the engine has done the matching and surfaced via the user's
 * own vote doc fields.
 */
export function buildUserBlockInfo({
  slots = [],
  userVoteDoc = null,
}) {
  const infoBySlotId = {};
  const windowsBySlotId = new Map();
  (slots || []).forEach((slot) => {
    const win = slotWindow(slot);
    if (win) windowsBySlotId.set(slot.id, win);
  });

  const reasonMap = userVoteDoc?.voteAutoReason || {};
  (slots || []).forEach((slot) => {
    const reason = reasonMap[slot.id] || null;
    // H13: gate on reason.kind, NOT voteAutoSource. The engine emits
    // reason.kind === "conflict" whenever a conflict exists for the slot,
    // regardless of source ("auto-conflict" for unvoted slots,
    // "auto-conflict-override" for manually-voted-over-conflict slots, or
    // even when auto-vote is off and the slot is just informational). The
    // prior gate on voteAutoSource === "auto-conflict" missed all the
    // override / manual-vote cases, so the UI dropped the amber "Conflict"
    // text the moment the user voted on a conflict slot.
    if (reason?.kind === "conflict") {
      // Only surface sourceSchedulerId when the conflict is from a busyWindow
      // (a real, finalized scheduler). Event-derived conflicts have refId =
      // eventId, which would route the legacy "View blocking session" button
      // to /scheduler/<eventId> — a 404.
      const firstBusy = Array.isArray(reason?.busyWindows) && reason.busyWindows[0];
      infoBySlotId[slot.id] = {
        sourceSchedulerId: firstBusy?.sourceSchedulerId || null,
        humanLabel: reason?.humanLabel || "Conflict",
      };
    }
  });

  return { infoBySlotId, windowsBySlotId };
}
